import React, { useState } from 'react';
import Swal from 'sweetalert2';
import { createUpdateCalendarLog } from '../objects/calendarLog';
import { parseDesc } from '../hooks/useEventos';
import { normISO, fmtHora, toInputValue, val } from '../utils/surgeryHelpers';

/* Campos que, si vienen "sueltos" en la descripción (por ejemplo, capturados
   desde el wizard de reservación al agendar una cirugía No Programada), se
   consolidan en los campos estructurados del evento al guardar este modal.
   Una vez consolidados, se retiran de la descripción para que los campos
   estructurados queden como única fuente de verdad. */
const CAMPOS_CONSOLIDABLES = [
  'TipoIngresoCirugia', 'DerechoCirugia', 'Reintervencion', 'NumeroHospitalizacion',
  'ObservacionTiempos', 'TiempoIngresaSala', 'TiempoIniciaAnestesia', 'TiempoIniciaCirugia',
  'TiempoTerminaCirugia', 'TiempoTerminaAnestesia', 'TiempoEgresaSala',
];

const limpiarDescripcionConsolidada = (description) => {
  if (!description) return description;
  return description
    .split('|')
    .filter((par) => !CAMPOS_CONSOLIDABLES.includes(par.split('=')[0]))
    .join('|');
};

const SurgeryTimesModal = ({ doc, userName, onClose, onSaved }) => {
  const ev = doc?.event || {};
  const fields = parseDesc(ev.description);

  const medico       = val(fields['Medico']);
  const paciente      = val(fields['Paciente']);
  const procedimiento = val(fields['Procedimiento']);

  const tq = ev.tiemposQuirurgicos || {};

  // Fecha/hora programada de inicio, usada como valor por defecto de cada
  // campo (si aún no se ha capturado) y como fecha mínima seleccionable.
  const inicioProgramado = toInputValue(ev.start?.dateTime); // "2026-07-02T09:30"
  const fechaMinima       = inicioProgramado ? inicioProgramado.slice(0, 10) : '';
  const minDateTime       = fechaMinima ? `${fechaMinima}T00:00` : undefined;

  const [tipoIngreso,    setTipoIngreso]    = useState(ev.tipoIngresoCirugia || fields['TipoIngresoCirugia'] || 'Programada');
  const [derechoQx,      setDerechoQx]      = useState(ev.derechoCirugia || fields['DerechoCirugia'] || 'Mayor');
  const [tipoAnestesia,  setTipoAnestesia]  = useState(ev.tipoAnestesia || fields['TipoAnestesia'] || 'General');
  const [anestesiaOtro,  setAnestesiaOtro]  = useState(ev.tipoAnestesiaOtro || '');
  const [reintervencion, setReintervencion] = useState(Boolean(ev.reintervencion) || fields['Reintervencion'] === 'Si');
  const [numeroHospitalizacion, setNumeroHospitalizacion] = useState(ev.numeroHospitalizacion || val(fields['NumeroHospitalizacion']) || '');
  const [observacionQx,  setObservacionQx]  = useState(ev.observacionTiempos || val(fields['ObservacionTiempos']) || '');

  const [tiempos, setTiempos] = useState({
    ingresaSala:      toInputValue(tq.ingresaSala)      || toInputValue(fields['TiempoIngresaSala'])      || inicioProgramado,
    iniciaAnestesia:  toInputValue(tq.iniciaAnestesia)  || toInputValue(fields['TiempoIniciaAnestesia'])  || inicioProgramado,
    iniciaCirugia:    toInputValue(tq.iniciaCirugia)    || toInputValue(fields['TiempoIniciaCirugia'])    || inicioProgramado,
    terminaCirugia:   toInputValue(tq.terminaCirugia)   || toInputValue(fields['TiempoTerminaCirugia'])   || inicioProgramado,
    terminaAnestesia: toInputValue(tq.terminaAnestesia) || toInputValue(fields['TiempoTerminaAnestesia']) || inicioProgramado,
    egresaSala:       toInputValue(tq.egresaSala)       || toInputValue(fields['TiempoEgresaSala'])       || inicioProgramado,
  });
  const setTiempo = (key, value) => setTiempos((prev) => ({ ...prev, [key]: value }));

  const [guardando, setGuardando] = useState(false);

  // Corresponde a la sección oculta arriba (Tipo de ingreso, Derecho de
  // cirugía, Tipo de anestesia, Tiempos, Re-intervención): mientras esté
  // en `false`, esos campos NO se envían al guardar, para no sobreescribir
  // con valores por defecto que nadie llegó a revisar/confirmar. Los
  // valores previos que ya existieran en el evento se conservan tal cual
  // (no se tocan). Cambiar a `true` junto con el bloque comentado arriba
  // para reactivar por completo.
  const MOSTRAR_TIEMPOS_QX_CAMPOS = false

  const handleGuardar = async () => {
    setGuardando(true)

    // Se conservan los valores actuales de fecha/hora y requerimientos: este modal
    // solo agrega la información de tiempos quirúrgicos y marca la cirugía
    // como Realizada, para que quede registrada en el histórico.
    const eventoClonado = {
      ...ev,
      start: { ...ev.start },
      end: { ...ev.end },
      summary:              'Realizada',
      numeroHospitalizacion: numeroHospitalizacion,
      observacionTiempos:   observacionQx,
      ...(MOSTRAR_TIEMPOS_QX_CAMPOS ? {
        tipoIngresoCirugia:   tipoIngreso,
        derechoCirugia:       derechoQx,
        tipoAnestesia:        tipoAnestesia,
        tipoAnestesiaOtro:    tipoAnestesia === 'Otro' ? anestesiaOtro : '',
        reintervencion:       reintervencion,
        tiemposQuirurgicos:   { ...tiempos },
      } : {}),
    };

    // La descripción se limpia de los campos que ya quedaron consolidados
    // arriba como campos estructurados del evento (evita datos duplicados).
    const descripcionLimpia = limpiarDescripcionConsolidada(ev.description);

    const ahora = new Date().toISOString();
    const fechaAnterior       = normISO(ev.start?.dateTime)?.split('T')[0] || '';
    const horaInicialAnterior = fmtHora(ev.start?.dateTime);
    const horaFinalAnterior   = fmtHora(ev.end?.dateTime);

    try {
      const res = await createUpdateCalendarLog(
        doc.id,                                  // eventId
        'update',                                 // peticion
        'Realizada',                                 // status
        eventoClonado,                              // event
        descripcionLimpia,                          // AppointmentType (limpia de campos ya consolidados)
        ev.start?.dateTime,                         // date (sin cambios)
        ev.end?.dateTime,                           // dateFinal (sin cambios)
        userName || fields['Solicitado'] || '',      // create
        ahora,                                       // time
        doc.id,                                      // id
        fechaAnterior,
        horaInicialAnterior,
        horaFinalAnterior,
        ev.imagenologia || {},                       // imagenologia (sin cambios)
        Array.isArray(ev.materialEspecial) ? ev.materialEspecial : [], // materialEspecial (sin cambios)
        Array.isArray(ev.rentaEquipos) ? ev.rentaEquipos : [],         // rentaEquipos (sin cambios)
        ev.muestrapatologica || {},                  // muestrapatologica (sin cambios)
        ev.pacienteId
      );

      setGuardando(false);

      if (res === 'success') {
        Swal.fire({ icon: 'success', title: 'Cirugía marcada como realizada', timer: 1600, showConfirmButton: false });
        onSaved && onSaved();
        onClose();
      } else {
        Swal.fire({ icon: 'error', title: 'No se pudo guardar', text: 'Intenta nuevamente.' });
      }
    } catch (err) {
      setGuardando(false);
      Swal.fire({ icon: 'error', title: 'No se pudo guardar', text: 'Intenta nuevamente.' });
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(17,24,39,.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100, padding: '20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '480px',
          maxHeight: '88vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 50px rgba(0,0,0,.25)',
        }}
      >
        {/* Header */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ color: '#0582a9', fontSize: '15px', fontWeight: 800 }}>✓ Cirugía realizada</span>
          <button
            onClick={onClose}
            aria-label="Cerrar"
            style={{ background: '#f3f4f6', border: 'none', borderRadius: '50%', color: '#374151', cursor: 'pointer', fontSize: '16px', height: '30px', width: '30px', lineHeight: 1 }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '18px 22px', overflowY: 'auto' }}>

          {/* Datos de identificación */}
          {(medico || paciente || procedimiento) && (
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '10px', marginBottom: '18px', padding: '12px 14px' }}>
              {medico && (
                <div style={{ marginBottom: (paciente || procedimiento) ? '8px' : 0 }}>
                  <IdLabel>Médico</IdLabel>
                  <span style={{ color: '#111827', display: 'block', fontSize: '13px', fontWeight: 700 }}>{medico}</span>
                </div>
              )}
              {paciente && (
                <div style={{ marginBottom: procedimiento ? '8px' : 0 }}>
                  <IdLabel>Paciente</IdLabel>
                  <span style={{ color: '#111827', display: 'block', fontSize: '13px', fontWeight: 700 }}>{paciente}</span>
                </div>
              )}
              {procedimiento && (
                <div>
                  <IdLabel>Procedimiento</IdLabel>
                  <span style={{ color: '#111827', display: 'block', fontSize: '13px', fontWeight: 700 }}>{procedimiento}</span>
                </div>
              )}
            </div>
          )}

          {/* Tipo de ingreso / Derecho de cirugía / Tipo de anestesia / Tiempos /
              Re-intervención — ocultos por el momento a petición. Se deja la
              lógica y el estado intactos (siguen con sus valores por defecto,
              que ya no se envían al guardar — ver handleGuardar). Para
              reactivar, descomentar este bloque completo. */}
          {/*
          <Label>Tipo de ingreso a cirugía</Label>
          <RadioGroup
            options={['Programada', 'No Programada']}
            value={tipoIngreso}
            onChange={setTipoIngreso}
          />

          <div style={{ height: '14px' }} />
          <Label>Derecho de cirugía</Label>
          <RadioGroup
            options={['Mayor', 'Menor']}
            value={derechoQx}
            onChange={setDerechoQx}
          />

          <div style={{ height: '14px' }} />
          <Label>Tipo de anestesia</Label>
          <RadioGroup
            options={['General', 'Bloqueo', 'Sedación', 'Otro']}
            value={tipoAnestesia}
            onChange={setTipoAnestesia}
          />
          {tipoAnestesia === 'Otro' && (
            <input
              type="text"
              value={anestesiaOtro}
              onChange={(e) => setAnestesiaOtro(e.target.value)}
              placeholder="Especifica el tipo de anestesia"
              style={{ ...inputStyle, marginTop: '8px' }}
            />
          )}

          <div style={{ height: '18px' }} />
          <Label>Tiempos</Label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <TiempoField label="Ingresa a sala"     value={tiempos.ingresaSala}      onChange={(v) => setTiempo('ingresaSala', v)}      min={minDateTime} />
            <TiempoField label="Inicia anestesia"   value={tiempos.iniciaAnestesia}  onChange={(v) => setTiempo('iniciaAnestesia', v)}  min={minDateTime} />
            <TiempoField label="Inicia cirugía"     value={tiempos.iniciaCirugia}    onChange={(v) => setTiempo('iniciaCirugia', v)}    min={minDateTime} />
            <TiempoField label="Termina cirugía"    value={tiempos.terminaCirugia}   onChange={(v) => setTiempo('terminaCirugia', v)}   min={minDateTime} />
            <TiempoField label="Termina anestesia"  value={tiempos.terminaAnestesia} onChange={(v) => setTiempo('terminaAnestesia', v)} min={minDateTime} />
            <TiempoField label="Egresa de sala"     value={tiempos.egresaSala}       onChange={(v) => setTiempo('egresaSala', v)}       min={minDateTime} />
          </div>

          <div style={{ height: '16px' }} />
          <Checkbox checked={reintervencion} onChange={setReintervencion} label="Re-intervención" />
          */}

          <div style={{ height: '14px' }} />
          <Label>Número de hospitalización</Label>
          <input
            type="text"
            value={numeroHospitalizacion}
            onChange={(e) => setNumeroHospitalizacion(e.target.value)}
            placeholder="Ej. 123456"
            style={inputStyle}
          />

          <div style={{ height: '14px' }} />
          <Label>Observaciones importantes</Label>
          <textarea
            value={observacionQx}
            onChange={(e) => setObservacionQx(e.target.value)}
            placeholder="Observaciones importantes sobre la cirugía realizada…"
            rows={3}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
          />
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', gap: '8px', padding: '14px 22px', borderTop: '1px solid #f3f4f6' }}>
          <button
            onClick={onClose}
            disabled={guardando}
            style={{ background: '#fff', border: '1px solid #d1d5db', borderRadius: '8px', color: '#374151', cursor: 'pointer', fontSize: '12px', fontWeight: 700, padding: '9px 16px' }}
          >
            Cancelar
          </button>
          <button
            onClick={handleGuardar}
            disabled={guardando}
            style={{
              marginLeft: 'auto', background: '#0582a9', border: 'none', borderRadius: '8px',
              color: '#fff', cursor: guardando ? 'default' : 'pointer', fontSize: '12px', fontWeight: 700,
              padding: '9px 18px', opacity: guardando ? .7 : 1,
            }}
          >
            {guardando ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  );
};

const inputStyle = {
  border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px',
  padding: '8px 10px', width: '100%', boxSizing: 'border-box',
};

const Label = ({ children }) => (
  <span style={{ color: '#6b7280', display: 'block', fontSize: '9px', fontWeight: 700, letterSpacing: '.5px', marginBottom: '6px', textTransform: 'uppercase' }}>
    {children}
  </span>
);

const IdLabel = ({ children }) => (
  <span style={{ color: '#9ca3af', display: 'block', fontSize: '9px', fontWeight: 700, letterSpacing: '.5px', marginBottom: '2px', textTransform: 'uppercase' }}>
    {children}
  </span>
);

const Checkbox = ({ checked, onChange, label }) => (
  <label style={{ alignItems: 'center', color: '#111827', cursor: 'pointer', display: 'flex', fontSize: '13px', fontWeight: 600, gap: '6px' }}>
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ accentColor: '#04afaa', height: '15px', width: '15px' }} />
    {label}
  </label>
);

const RadioGroup = ({ options, value, onChange }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', marginTop: '4px' }}>
    {options.map((opt) => (
      <label key={opt} style={{ alignItems: 'center', color: '#111827', cursor: 'pointer', display: 'flex', fontSize: '13px', fontWeight: 600, gap: '6px' }}>
        <input
          type="radio"
          checked={value === opt}
          onChange={() => onChange(opt)}
          style={{ accentColor: '#0582a9', height: '14px', width: '14px' }}
        />
        {opt}
      </label>
    ))}
  </div>
);

const TiempoField = ({ label, value, onChange, min }) => (
  <div>
    <span style={{ color: '#6b7280', display: 'block', fontSize: '10px', fontWeight: 600, marginBottom: '4px' }}>{label}</span>
    <input
      type="datetime-local"
      value={value}
      min={min}
      onChange={(e) => onChange(e.target.value)}
      style={{ ...inputStyle, fontSize: '12px', padding: '6px 8px' }}
    />
  </div>
);

export default SurgeryTimesModal;